import type {Component, Editor} from 'grapesjs';
import {isUserLoggedIn} from '@Libraries/user.library';
import {enterVersionHistoryMode, exitVersionHistoryMode, revertToCurrentVersion} from '@Components/email-maker-version-history-panel/email-maker-version-history-panel.library';
import {getURISegment} from '@Utils/url.util';
import {hasDatePassed2} from '@Utils/date.util';
import {unmountAndRemoveElement} from '@Utils/root-element.util';

const VERSION_HISTORY_CTA_SELECTOR = '.js-email-version-history-cta';
const STYLE_MANAGER_VERSION_HISTORY_CLASS = '-version-history';

const PULSE_DEADLINE = '2026-08-20';
const PULSE_SELECTOR = '.js-email-version-history-pulse';
const HIDDEN_CLASS = '_hidden';

const SAVE_PILL_SELECTOR = '.js-auto-save-container';
const POPUP_WIDTH = 328;
const PADDING_FROM_PILL = 6;
const POPUP_Z_INDEX = 5000;

interface EmailVersionHistoryOnboardingResponse {
  hasSeen: boolean;
}

let versionHistorySidePanel: HTMLElement | null = null;
let hasAttemptedOnboarding = false;
let onboardingContainer: HTMLElement | null = null;

/** Whether the editor is editing an existing saved template (URL mode is `load`) — the structural precondition for version history. */
export function isEditorInLoadState(): boolean {
  return getURISegment(1) === window.PMW.emails.MODE_LOAD;
}

/** Whether an AI redesign is currently running (redux flag held for the command's full duration; see CMDS.AI_REDESIGN_EMAIL_CAMPAIGN). */
function isAiRedesignInProgress(): boolean {
  return window.PMW.redux.store.getState().emailMakerAiRedesignPromptBox.isLoading;
}

/** Whether a color palette is currently being applied to the email (set during the auto-branding apply flow; see custom-style-types/auto-branding.ts). */
function isColorPaletteBeingApplied(): boolean {
  return window.PMW.redux.store.getState().colorPalettes.colorPaletteBeingApplied !== undefined;
}

/** Whether a canvas-mutating operation (AI redesign or color-palette application) is running, during which version history must stay closed. */
function isVersionHistoryBlockedByActiveOperation(): boolean {
  return isAiRedesignInProgress() || isColorPaletteBeingApplied();
}

/** Whether version history can be opened right now: editor in load state and no canvas-mutating operation in progress. */
export function isVersionHistoryAccessible(): boolean {
  return isEditorInLoadState() && !isVersionHistoryBlockedByActiveOperation();
}

/** Enables the version-history CTA(s); gated on isEditorInLoadState (not isVersionHistoryAccessible) so an operation's end-of-run re-enable isn't no-op'd while its in-progress flag still lingers. */
export function enableVersionHistoryCta(): void {
  if (!isEditorInLoadState()) {
    return;
  }

  document.querySelectorAll(VERSION_HISTORY_CTA_SELECTOR).forEach((cta): void => {
    cta.classList.remove('-disabled', 'has-tooltip');
    cta.removeAttribute('title');
    cta.removeAttribute('aria-disabled');
  });
}

/** Disables the version-history CTA(s) while an AI redesign runs (greyed, non-interactive); reversed by enableVersionHistoryCta when it ends. */
export function disableVersionHistoryCta(): void {
  document.querySelectorAll(VERSION_HISTORY_CTA_SELECTOR).forEach((cta): void => {
    cta.classList.add('-disabled');
    cta.setAttribute('aria-disabled', 'true');
  });
}

/** Restores the add-block/style-manager UI and unmounts the side panel (deferred a tick so it's safe to call from inside the panel's own React tree). */
const cleanupVersionHistoryDom = (): void => {
  document.querySelector('.js-add-block-dialog')?.classList.remove('_invisible');
  document.querySelector('.js-style-manager-container')?.classList.remove(STYLE_MANAGER_VERSION_HISTORY_CLASS);

  const panel = versionHistorySidePanel;
  if (!panel) {
    return;
  }
  versionHistorySidePanel = null;
  window.setTimeout((): void => {
    unmountAndRemoveElement(panel);
  }, 0);
};

export const exitVersionHistory = (editor: Editor): void => {
  revertToCurrentVersion(editor);
  exitVersionHistoryMode(editor);
  cleanupVersionHistoryDom();
};

export const openVersionHistory = (editor: Editor): void => {
  if (!isVersionHistoryAccessible()) {
    return;
  }

  const idEmailTemplate = window.PMW.idEmailTemplate;
  if (!idEmailTemplate) {
    return;
  }

  if (document.getElementById(window.PMW.emails.MORE_OPTIONS_DIALOG)) {
    editor.runCommand(window.PMW.emails.commands.CLOSE_MORE_OPTIONS);
  }
  dismissEmailVersionHistoryOnboardingPopup();

  const wrapper = editor.runCommand(window.PMW.emails.commands.GET_MAIN_WRAPPER) as Component;
  editor.select(wrapper);

  editor.runCommand(window.PMW.emails.commands.SET_AUTOSAVE, {autosave: false});
  window.PMW.emails.IS_IN_VERSION_HISTORY_MODE = true;

  enterVersionHistoryMode(editor);

  versionHistorySidePanel = document.createElement('div');
  versionHistorySidePanel.style.position = 'absolute';
  versionHistorySidePanel.style.inset = '0';
  versionHistorySidePanel.style.paddingBottom = '0';
  window.PMW.renderVersionHistorySidePanel(versionHistorySidePanel, {editor, idEmailTemplate, onExit: cleanupVersionHistoryDom});

  const blocksManagerContainer = document.getElementById(window.PMW.emails.blocks.ID_BLOCKS_MANAGER_CONTAINER);
  blocksManagerContainer?.classList.add('_invisible');

  document.querySelector('.js-add-block-dialog')?.classList.add('_invisible');
  document.querySelector('.js-style-manager-container')?.classList.add(STYLE_MANAGER_VERSION_HISTORY_CLASS);

  editor.runCommand(window.PMW.emails.commands.OPEN_SIDE_PANEL, {
    title: window.i18next.t('pmwjs_version_history'),
    content: versionHistorySidePanel,
    hideBackButton: true,
    onClose: (): void => {
      exitVersionHistory(editor);
    },
  });

  editor.runCommand(window.PMW.emails.commands.MAKE_STYLEMANAGER_ACTIVE);
};

/** Tears down the onboarding popup if it is open. */
export const dismissEmailVersionHistoryOnboardingPopup = (): void => {
  const container = onboardingContainer;
  if (!container) {
    return;
  }
  onboardingContainer = null;
  window.setTimeout((): void => {
    window.PMW.unmountVersionHistoryOnboardingPopup(container);
    container.remove();
  }, 0);
};

const markEmailVersionHistoryOnboardingAsSeen = async (): Promise<void> => {
  try {
    await window.PMW.writeLocal('user/updateUserHasSeenEmailVersionHistoryOnboarding');
  } catch (error) {
    console.error('Failed to mark email version history onboarding as seen:', error);
  }
};

const mountOnboardingPopupBelowPill = (savePill: HTMLElement): void => {
  const rect = savePill.getBoundingClientRect();
  const container = document.createElement('div');
  container.style.left = `${rect.left + rect.width / 2 - POPUP_WIDTH / 2}px`;
  container.style.position = 'fixed';
  container.style.top = `${rect.bottom + PADDING_FROM_PILL}px`;
  container.style.width = `${POPUP_WIDTH}px`;
  container.style.zIndex = `${POPUP_Z_INDEX}`;
  document.body.appendChild(container);

  onboardingContainer = container;
  window.PMW.renderVersionHistoryOnboardingPopup(container, {onClose: dismissEmailVersionHistoryOnboardingPopup});
};

export const showEmailVersionHistoryOnboarding = async (): Promise<void> => {
  if (!isEditorInLoadState()) {
    return;
  }

  if (window.PMW.isMobileScreenWidth()) {
    return;
  }

  if (hasAttemptedOnboarding) {
    return;
  }
  hasAttemptedOnboarding = true;

  if (!isUserLoggedIn()) {
    return;
  }

  const savePill = document.querySelector<HTMLElement>(SAVE_PILL_SELECTOR);
  if (!savePill) {
    return;
  }

  try {
    const response = (await window.PMW.readLocal('user/hasUserSeenEmailVersionHistoryOnboarding')) as EmailVersionHistoryOnboardingResponse;
    if (response.hasSeen) {
      return;
    }

    mountOnboardingPopupBelowPill(savePill);
    void markEmailVersionHistoryOnboardingAsSeen();
  } catch (error) {
    console.error('Failed to show email version history onboarding:', error);
  }
};

export const showEmailVersionHistoryPulse = (): void => {
  if (hasDatePassed2(new Date(PULSE_DEADLINE))) {
    return;
  }

  document.querySelector(PULSE_SELECTOR)?.classList.remove(HIDDEN_CLASS);
};
